import React, {FC, memo, useState} from "react";
import {Button, Card, Checkbox, Modal, Space, Tooltip, message} from "antd";
import {InfoCircleTwoTone, SwapOutlined} from "@ant-design/icons";
import {red} from "@ant-design/colors";
import {
    BookingType,
    GetBookedAttendeesByCourseIdDocument,
    GetBookingsByCourseDocument,
    GetBookingsByCourseQuery,
    GetCourseByIdDocument,
    GetCourseDetailsByIdDocument,
    GetWaitlistByCourseDocument,
    TablesEnum,
    TransferBookingInput,
    TransferMultipleBookingsInput,
    useGetBookingsByCourseQuery,
    useTransferMultipleBookingsMutation,
    useVoidMolliePaymentLinksForBookingsMutation,
} from "../../../generated/graphql";
import {marginL, panelWidth} from "../../../styles/layout";
import TransferCourseSelector from "../../invoice/MoreMenuCourseDetails/TransferMultipleAttendees/TransferCourseSelector";
import {TableType} from "../../courses/CourseEdit/CourseListColumns";
import {TableType as BookingTableType} from "../../bookings/BookingList/BookingListColumns";
import css from "./TransferMultipleBookings.less";
import {useBookingsInput} from "../../invoice/MoreMenuCourseDetails/TransferMultipleAttendees/useTransferMultipleBookingsInput";
import TransferDragAndDropDescription from "../../invoice/MoreMenuCourseDetails/TransferMultipleAttendees/TransferDragAndDropDescription";
import i18n from "../../../services/i18n";
import BookingList from "../../bookings/BookingList/BookingList";
import {KnownError, handleError} from "../../../errors/handleErrors";
import useTransferWizardMode from "../../../hooks/useTransferWizardMode";
import {useStaffVisibility} from "../../../hooks/useStaffVisibility";
import {StaffVisibilityLevel} from "../../../../shared/models/staffVisibility";
import BulkTransferWizard from "./BulkTransferWizard";
import BulkDunningAlert from "../../invoice/SettlementDetails/BookingDunnings/BulkDunningAlert";

type TransferMultipleBookingsProps = {
    currentCourseId: string;
    key: number;
    selectedBookings: GetBookingsByCourseQuery["getBookingsByCourse"]["items"];
    btnClassName?: string;
    onFinished: () => void;
};

type BookingItem = GetBookingsByCourseQuery["getBookingsByCourse"]["items"][number];

// Transfer-FK / Link-Void: Terminale (= nicht-live) Mollie-Status. Ein Attempt in
// einem dieser Status blockiert die Umbuchung NICHT (Whitelist-Konvention).
// Alles andere (open/pending + unbekannte/neue Status) mit gesetztem Link gilt
// als live/blockierend.
//
// MUSS byte-genau mit der Backend-Quelle der Wahrheit übereinstimmen:
// `TERMINAL_MOLLIE_STATUSES` in src/server/scheduledPayment/scheduledPaymentTombstoneGuard.ts.
// Weicht die Liste ab und enthält hier MEHR Werte als das Backend, stuft das
// Frontend eine Buchung fälschlich als übertragbar ein, die der Backend-Guard
// blockiert → der gesamte (Alles-oder-nichts-)Bulk bricht fail-loud ab.
const TERMINAL_MOLLIE_STATUSES = [
    "paid",
    "superseded",
    "creation_failed",
    "failed",
    "expired",
    "canceled",
    "link_voided",
];

// Transfer-FK / Link-Void: Buchung wurde bereits über einen Mollie-Zahlungs-LINK
// bezahlt → NICHT auto-entwertbar (B-223, echtes Geld). Erkennung: ein Attempt mit
// gesetztem molliePaymentLinkId UND (status "paid" ODER gesetztem paidAt).
//
// Der molliePaymentLinkId-Check ist zwingend: normal (per Checkout/SEPA) bezahlte
// Buchungen tragen ebenfalls einen "paid"-Attempt, aber OHNE Zahlungslink — sie
// sind regulär übertragbar und dürfen nicht fälschlich blockiert werden (der
// Backend-Guard betrachtet ebenfalls nur Attempts mit gesetztem molliePaymentLinkId).
const isBlockedByPaidMollie = (booking: BookingItem): boolean => {
    const attempts = booking.molliePaymentAttempts ?? [];

    return attempts.some((attempt) => {
        const linkSet = attempt.molliePaymentLinkId !== null && attempt.molliePaymentLinkId !== undefined;
        const isPaid = attempt.status === "paid" || (attempt.paidAt !== null && attempt.paidAt !== undefined);

        return linkSet && isPaid;
    });
};

// Transfer-FK / Link-Void: Buchung hat einen live (entwertbaren) Mollie-Zahlungslink.
// Bedingung: Attempt mit gesetztem molliePaymentLinkId UND nicht-terminalem Status
// UND (linkExpiresAt null ODER in Zukunft) UND paidAt == null.
const hasActiveMolliePaymentLink = (booking: BookingItem): boolean => {
    const attempts = booking.molliePaymentAttempts ?? [];

    return attempts.some((attempt) => {
        const linkSet = attempt.molliePaymentLinkId !== null && attempt.molliePaymentLinkId !== undefined;
        const notTerminal = TERMINAL_MOLLIE_STATUSES.indexOf(attempt.status) === -1;
        const notExpired = attempt.linkExpiresAt === null || attempt.linkExpiresAt === undefined || new Date(attempt.linkExpiresAt) > new Date();
        const notPaid = attempt.paidAt === null || attempt.paidAt === undefined;

        return linkSet && notTerminal && notExpired && notPaid;
    });
};

// Transfer-FK / Link-Void (B-6967): Buchung hat eine offene DIREKTE Mollie-Zahlung
// (Payments-API, z.B. banktransfer — molliePaymentLinkId IS NULL), die der Backend-
// Guard `assertNoActivePaymentLink` seit PR #211 blockiert und die
// `voidMolliePaymentLinkForBooking` via `cancelOpenMollieDirectPaymentsForBooking`
// aktiv bei Mollie abbricht. Ohne diese Erkennung landet so eine Buchung faelschlich
// im "uebertragbar"-Bucket, der Server wirft dann MOLLIE_ACTIVE_PAYMENT_LINK und der
// Nutzer sieht nur eine Warnung statt der Ein-Klick-Aktion "entwerten und verschieben".
//
// Praedikat MUSS mit dem Backend-Cancel deckungsgleich sein, sonst wird ein Void→Move-
// Button angeboten, den der Void nicht clearen kann (Dead-End):
//   - nur der aktuelle Attempt (isCurrentAttempt),
//   - Status open/authorized — `pending` ist bei Mollie NICHT stornierbar und zaehlt
//     deshalb bewusst NICHT als entwertbar. pending-direkte Buchungen bleiben
//     uebertragbar: der Server tombstoned die betroffene Rate beim Transfer
//     (pending-Orphan-Fix in calculateScheduledPayments), statt sie zu loeschen,
//   - unbezahlt (paidAt == null).
// ponytail: tr_-ID-Check (isRealMolliePaymentId, Backend) weggelassen — ein direkter,
// aktueller, offener Attempt traegt IMMER eine echte tr_-ID (Platzhalter pl-pending-/
// failed- sind entweder Link-Attempts oder isCurrentAttempt=false/terminal). Bricht die
// Invariante, molliePaymentId in die GetBookingsByCourse-Selection aufnehmen und hier pruefen.
export const hasOpenDirectMolliePayment = (booking: BookingItem): boolean => {
    const attempts = booking.molliePaymentAttempts ?? [];

    return attempts.some((attempt) => {
        const isDirect = attempt.molliePaymentLinkId === null || attempt.molliePaymentLinkId === undefined;
        const isCurrent = attempt.isCurrentAttempt === true;
        const isCancelable = attempt.status === "open" || attempt.status === "authorized";
        const notPaid = attempt.paidAt === null || attempt.paidAt === undefined;

        return isDirect && isCurrent && isCancelable && notPaid;
    });
};

// Transfer-FK / Link-Void: Synchroner Builder für den Mutation-Payload (entspricht
// dem bookingList-Zweig von useBookingsInput, das als Hook nicht im async-Handler
// aufrufbar ist). Waitlist/PlaceReservation sind in diesem Dialog immer leer.
const buildBookingInput = (bookings: GetBookingsByCourseQuery["getBookingsByCourse"]["items"], originIds: Array<string>): Array<TransferBookingInput> => {
    const result: Array<TransferBookingInput> = [];
    bookings.forEach((item) => {
        if (originIds.indexOf(item.id) !== -1) {
            result.push({
                bookingId: item.id,
                originBookingType: item.bookingType as BookingType,
                targetBookingType: BookingType.Booking,
                usePlaceReservation: false,
                isSettled: item.invoice !== null,
            });
        }
    });

    return result;
};

const TransferMultipleBookings: FC<TransferMultipleBookingsProps> = ({currentCourseId, selectedBookings, key, btnClassName, onFinished}) => {
    const [open, setOpen] = useState(false);
    const [selectedCourse, setSelectedCourse] = useState<TableType | null>(null);
    const [deleteNotes, setDeleteNotes] = useState<boolean>(false);
    const [transfering, setTransfering] = useState(false);
    const [descriptionOpen, setDescriptionOpen] = useState(false);
    const [transferableBookings, setTransferableBookings] = useState<GetBookingsByCourseQuery["getBookingsByCourse"]["items"]>(selectedBookings);
    const [selectedBookingsWithDuplicates, setSelectedBookingsWithDuplicates] = useState<GetBookingsByCourseQuery["getBookingsByCourse"]["items"]>(selectedBookings);
    // Transfer-FK / Link-Void: Buchungen mit live (entwertbarem) Zahlungslink bzw.
    // bereits bezahltem (nicht übertragbarem) Zahlungslink.
    const [bookingsWithActiveLink, setBookingsWithActiveLink] = useState<GetBookingsByCourseQuery["getBookingsByCourse"]["items"]>([]);
    const [bookingsBlockedByPaidMollie, setBookingsBlockedByPaidMollie] = useState<GetBookingsByCourseQuery["getBookingsByCourse"]["items"]>([]);
    // #666: Im Wizard-Modus (Superuser-Flag + Einstellung „Neues Verfahren“) läuft „Verschieben“
    // über den Bulk-Wizard der Engine; „Kopieren“ bleibt Legacy (Weitermeldung, keine Geldbewegung).
    // Während der Modus lädt, ist der Verschieben-Knopf gesperrt — `false` heißt hier nicht Legacy.
    const {wizardMode, loading: transferModeLoading} = useTransferWizardMode();
    const [wizardBookingIds, setWizardBookingIds] = useState<Array<string> | null>(null);
    // Sichtbarkeitsregel „Buchungsaktionen: Nein“ (#1188): Sammel-Umbuchen/-Kopieren nicht anbieten.
    const bookingActionsDisabled = useStaffVisibility("bookingActions") === StaffVisibilityLevel.NoAccess;

    const {data: bookingsOfTargetCourseData, loading: loadingBookingsOfTargetCourse} = useGetBookingsByCourseQuery({
        skip: !selectedCourse,
        variables: {id: selectedCourse?.id ?? ""},
        // Zielkurs immer frisch: nicht jede Buchungsaktion lädt GetBookingsByCourse mit {id} nach
        fetchPolicy: "network-only",
    });

    React.useEffect(() => {
        const attendeeIdsOfTargetCourse = bookingsOfTargetCourseData?.getBookingsByCourse?.items.filter((booking) => booking.canceled === null).map((booking) => booking.attendee?.id);
        const bookingIdsOfDuplicateAttendees = selectedBookings.filter((booking) => attendeeIdsOfTargetCourse?.includes(booking.attendee?.id)).map((booking) => booking.id);
        const selectedBookingsWithoutDuplicates = selectedBookings.filter((booking) => !bookingIdsOfDuplicateAttendees.includes(booking.id));
        const selectedBookingsWithDuplicates = selectedBookings.filter((booking) => bookingIdsOfDuplicateAttendees.includes(booking.id));

        // Transfer-FK / Link-Void: 3-Wege-Aufteilung der nicht-Dubletten:
        // (1) bereits bezahlt (Mollie) → blockiert, (2) offene Mollie-Zahlung
        // (Zahlungslink ODER direkte Zahlung, z.B. banktransfer) → entwertbar,
        // (3) Rest → direkt übertragbar.
        const isVoidable = (booking: BookingItem): boolean => hasActiveMolliePaymentLink(booking) || hasOpenDirectMolliePayment(booking);
        const blockedByPaid = selectedBookingsWithoutDuplicates.filter((booking) => isBlockedByPaidMollie(booking));
        const withActiveLink = selectedBookingsWithoutDuplicates.filter((booking) => !isBlockedByPaidMollie(booking) && isVoidable(booking));
        const plainTransferable = selectedBookingsWithoutDuplicates.filter((booking) => !isBlockedByPaidMollie(booking) && !isVoidable(booking));

        const transferableBookings = selectedCourse && !loadingBookingsOfTargetCourse ? plainTransferable : selectedBookings;

        setSelectedBookingsWithDuplicates(selectedBookingsWithDuplicates);
        setBookingsBlockedByPaidMollie(selectedCourse && !loadingBookingsOfTargetCourse ? blockedByPaid : []);
        setBookingsWithActiveLink(selectedCourse && !loadingBookingsOfTargetCourse ? withActiveLink : []);
        setTransferableBookings(transferableBookings);
    }, [selectedCourse, selectedBookings, bookingsOfTargetCourseData, loadingBookingsOfTargetCourse]);
    const transferableBookingIds = transferableBookings.map((booking) => booking.id);
    const transferableBookingsCount = transferableBookings.length;

    const bookingInput: Array<TransferBookingInput> = useBookingsInput({
        bookings: transferableBookings,
        waitlist: undefined,
        originIds: transferableBookingIds,
        pReservations: undefined,
    });

    // const bookingInput: Array<TransferBookingInput> = useBookingsInput({
    //     bookings: selectedBookings,
    //     waitlist: undefined,
    //     originIds: selectedBookingIds,
    //     pReservations: undefined,
    // });
    const transferMultipleBookingsInput: TransferMultipleBookingsInput = {
        originCourseId: currentCourseId,
        targetCourseId: selectedCourse?.id ?? "",
        notifyBooker: false,
        copyBookings: false,
        bookings: bookingInput,
    };

    const knownErrors: Array<KnownError> = [
        {
            key: "Already enrolled",
            title: "Doppelbuchung",
            msg: `
                Mindestens einer der gewählten Teilnehmer:innen ist bereits in diesem Kurs gebucht, oder steht auf dessen Warteliste.
                Die Umbuchung kann deshalb nicht durchgeführt werden.
                `,
        },
        {
            // Transfer-FK / Link-Void: Server-Sicherheitsnetz bei Race (Link wird
            // zwischen Pre-Flight und Mutation aktiv). handleError matcht auf die
            // Message; daher zusätzlich Code-Extraktion im catch (siehe unten).
            key: "MOLLIE_ACTIVE_PAYMENT_LINK",
            title: i18n.containers.bookings.TransferMolliePaymentLink.bulk.knownErrorTitle(),
            msg: i18n.containers.bookings.TransferMolliePaymentLink.bulk.knownErrorMessage(),
        },
        {
            key: "default",
            title: "Fehler beim Verschieben der Buchungen",
            msg: "Sollte das Problem weiterhin bestehen, kontaktiere bitte den Support.",
        },
    ];

    const [transferMultipleBookings] = useTransferMultipleBookingsMutation({
        refetchQueries: [
            "GetBookingsByCourse",
            {query: GetBookingsByCourseDocument, variables: {id: currentCourseId}},
            "getCourseById",
            {query: GetCourseByIdDocument, variables: {id: currentCourseId}},
            "GetBookingsOverviewOptimized",
            "GetUnsettledBookings",
            "GetSettledBookings",
            "GetBookedAttendeesByCourseId",
            {query: GetBookedAttendeesByCourseIdDocument, variables: {courseId: currentCourseId}},
            "GetWaitlistByCourse",
            {query: GetWaitlistByCourseDocument, variables: {id: currentCourseId}},
            "GetCourseDetailsById",
            {query: GetCourseDetailsByIdDocument, variables: {id: currentCourseId}},
        ],
    });

    // Transfer-FK / Link-Void: Entwertet die aktiven Mollie-Zahlungslinks der
    // betroffenen Buchungen, bevor diese verschoben werden. Best-Effort pro
    // Buchung serverseitig (Promise.allSettled). Gleicher Refetch-Scope wie die
    // Transfer-Mutation.
    const [voidMolliePaymentLinks] = useVoidMolliePaymentLinksForBookingsMutation({
        refetchQueries: [
            "GetBookingsByCourse",
            {query: GetBookingsByCourseDocument, variables: {id: currentCourseId}},
            "GetCourseDetailsById",
            {query: GetCourseDetailsByIdDocument, variables: {id: currentCourseId}},
        ],
    });

    // Transfer-FK / Link-Void: Extrahiert den GraphQL-Error-Code (ApolloError legt
    // den Code direkt in extensions.code ab). handleError matcht nur die Message,
    // daher hier separat — robuster als Message-Match (R3).
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const extractGraphQlErrorCode = (error: any): string | undefined => {
        return error && error.graphQLErrors && error.graphQLErrors[0] && error.graphQLErrors[0].extensions ? error.graphQLErrors[0].extensions.code : undefined;
    };

    const handleTransferMultipleBookings = async ({action, bookings}: {action: "copy" | "move"; bookings?: GetBookingsByCourseQuery["getBookingsByCourse"]["items"]}) => {
        // #666: Verschieben im Wizard-Modus → Bulk-Wizard statt Legacy-Mutation (auch nach dem
        // Entwerten der Zahlungslinks). Der Wizard schreibt erst in seiner Vorschau.
        if (action === "move" && wizardMode) {
            setTransfering(false);
            setWizardBookingIds((bookings ?? transferableBookings).map((booking) => booking.id));

            return;
        }
        setTransfering(true);
        message.loading({
            content: "Buchungen werden verschoben...",
            key: "transferMultipleBookings",
        });

        // Transfer-FK / Link-Void: Beim Void→Move-Pfad wird ein erweiterter
        // Buchungssatz (übertragbar + entwertet) übergeben; sonst die normale
        // transferableBookings-Menge.
        const bookingsToTransfer = bookings ?? transferableBookings;
        const bookingIdsToTransfer = bookingsToTransfer.map((booking) => booking.id);
        const dynamicBookingInput = buildBookingInput(bookingsToTransfer, bookingIdsToTransfer);

        const dynamicTransferInput: TransferMultipleBookingsInput = {
            ...transferMultipleBookingsInput,
            copyBookings: action === "copy",
            bookings: dynamicBookingInput,
        };

        try {
            await transferMultipleBookings({
                variables: {
                    transferMultipleBookingsInput: dynamicTransferInput,
                    deleteNotes,
                },
            });
            await message.success({
                content: "Buchungen wurden erfolgreich verschoben",
                key: "transferMultipleBookings",
                duration: 3,
            });
        } catch (error) {
            // Transfer-FK / Link-Void: Server-Sicherheitsnetz bei Race — wenn der
            // Code MOLLIE_ACTIVE_PAYMENT_LINK kommt, gezielten Hinweis zeigen,
            // sonst generisches handleError.
            if (extractGraphQlErrorCode(error) === "MOLLIE_ACTIVE_PAYMENT_LINK") {
                Modal.warning({
                    title: i18n.containers.bookings.TransferMolliePaymentLink.bulk.knownErrorTitle(),
                    content: i18n.containers.bookings.TransferMolliePaymentLink.bulk.knownErrorMessage(),
                });
            } else {
                handleError("transferAttendeeToCourseSimple", error, "modal", knownErrors);
            }
            await message.error({
                content: "Fehler beim Verschieben der Buchungen",
                key: "transferMultipleBookings",
            });
            console.error("Error transferring bookings", error);
        } finally {
            setTransfering(false);
            // resetAll();
            message.destroy("transferMultipleBookings");
            onFinished();
            setOpen(false);
        }
    };

    // Transfer-FK / Link-Void: Entwertet die aktiven Zahlungslinks und verschiebt
    // danach automatisch (übertragbare + entwertete Buchungen). Bezahlte
    // (blockierte) Buchungen sind ausgeschlossen.
    const handleVoidAndMove = async () => {
        if (bookingsWithActiveLink.length === 0) return;
        setTransfering(true);
        message.loading({
            content: i18n.containers.bookings.TransferMolliePaymentLink.bulk.voidingMessage(),
            key: "voidMolliePaymentLinks",
        });

        try {
            await voidMolliePaymentLinks({
                variables: {bookingIds: bookingsWithActiveLink.map((booking) => booking.id)},
            });
            message.success({
                content: i18n.containers.bookings.TransferMolliePaymentLink.bulk.voidSuccessMessage(),
                key: "voidMolliePaymentLinks",
                duration: 3,
            });
            const bookingsForMove = transferableBookings.concat(bookingsWithActiveLink);
            await handleTransferMultipleBookings({action: "move", bookings: bookingsForMove});
        } catch (error) {
            message.error({
                content: i18n.containers.bookings.TransferMolliePaymentLink.bulk.voidErrorMessage(),
                key: "voidMolliePaymentLinks",
            });
            setTransfering(false);
        }
    };

    const Footer = () => {
        // const bookingCount = selectedBookings.length;
        const bookingCount = transferableBookings.length;
        // Transfer-FK / Link-Void: Buchungen mit live Zahlungslink zählen erst nach
        // dem Entwerten zu den verschiebbaren. Der Move-Button ist auch dann aktiv,
        // wenn nur entwertbare (aber keine direkt übertragbaren) Buchungen vorliegen.
        const activeLinkCount = bookingsWithActiveLink.length;
        const moveDisabled = !currentCourseId || !selectedCourse || (bookingCount === 0 && activeLinkCount === 0) || transferModeLoading;
        const copyDisabled = !currentCourseId || !selectedCourse || bookingCount === 0;

        const tooltipText = moveDisabled ? (bookingCount === 0 && activeLinkCount === 0 ? "Es gibt keine übertragbaren Teilnehmer" : "Bitte wähle zuerst den Zielkurs") : undefined;

        return (
            <Space className={css.footer}>
                <Checkbox checked={deleteNotes} onChange={(e) => setDeleteNotes(e.target.checked)} style={{marginLeft: 10, marginRight: 10}}>
                    {`Notizen löschen`}
                    <Tooltip title="Wenn aktiviert, werden die Notizen sowohl des Buchers als auch der Mitarbeiter in den neuen Buchungen entfernt.">
                        <InfoCircleTwoTone style={{marginLeft: 5}} />
                    </Tooltip>
                </Checkbox>
                {/* Transfer-FK / Link-Void: Wenn live Zahlungslinks vorhanden sind, ersetzt
                    der Void→Move-Button den normalen Verschieben-Button (verschiebt
                    übertragbare + entwertete). */}
                {activeLinkCount > 0 ? (
                    <Tooltip title={tooltipText}>
                        <Button
                            loading={transfering || loadingBookingsOfTargetCourse}
                            disabled={moveDisabled || transfering}
                            type="default"
                            onClick={async () => handleVoidAndMove()}
                        >
                            {i18n.containers.bookings.TransferMolliePaymentLink.bulk.voidAndMoveButton({count: String(transferableBookingsCount + activeLinkCount)})}
                        </Button>
                    </Tooltip>
                ) : (
                    <Tooltip title={tooltipText}>
                        <Button
                            loading={transfering || loadingBookingsOfTargetCourse}
                            disabled={moveDisabled || transfering}
                            type="default"
                            onClick={async () => handleTransferMultipleBookings({action: "move"})}
                        >
                            {`${transferableBookingsCount} Buchungen verschieben`}
                        </Button>
                    </Tooltip>
                )}
                <Tooltip title={tooltipText}>
                    <Button
                        loading={transfering || loadingBookingsOfTargetCourse}
                        disabled={copyDisabled || transfering}
                        type="primary"
                        onClick={async () => handleTransferMultipleBookings({action: "copy"})}
                    >
                        {`${transferableBookingsCount} Buchungen kopieren`}
                    </Button>
                </Tooltip>
            </Space>
        );
    };

    const renderTable = (bookings: Array<BookingTableType>, duplicateTable?: boolean) => {
        const additionalStyle = duplicateTable
            ? {
                  marginTop: -10,
                  border: `4px solid ${red[2]}`,
                  padding: 0,
                  borderRadius: 7,
                  overFlow: "hidden",
                  opacity: 0.8,
              }
            : undefined;

        return (
            <BookingList
                hideColumnSelector={duplicateTable}
                tablesEnum={TablesEnum.TransferSimpleCourseSelector}
                tableData={{
                    dataSource: selectedCourse ? bookings : (selectedBookings as unknown as Array<BookingTableType>),
                    existMore: false,
                    total: bookings.length,
                    loading: false,
                }}
                refetch={() => console.log("refetch")}
                setOptions={() => console.log("setOptions")}
                toolBarButtons={[]}
                hideCreate
                hideHeader
                hidePagination
                hideFiltersAndSorters
                notSelectable
                minPadding
                defaultColumns={["bookingNumber", "attendeeFullName", "age", "bookerFullName", "email", "bookedBy", "billed"]}
                hiddenColumns={["actions"]}
                actionColumn={() => null}
                style={{
                    marginTop: -25,
                    ...additionalStyle,
                }}
            />
        );
    };

    if (bookingActionsDisabled) {
        return null;
    }

    if (open) {
        return (
            <Modal
                title={
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                        }}
                    >
                        <div>{"Verschieben oder Kopieren der ausgewählten Teilnehmern"}</div>
                        <Space>
                            <TransferDragAndDropDescription setDescriptionOpen={setDescriptionOpen} descriptionOpen={descriptionOpen} descritpion="simple" />
                            <Button
                                type="primary"
                                style={{
                                    backgroundColor: red[3],
                                    borderColor: red[3],
                                    fontWeight: 450,
                                }}
                                onClick={() => setOpen(false)}
                            >
                                {i18n.containers.customers.BookerDetails.modal.abortShort()}
                            </Button>
                        </Space>
                    </div>
                }
                open={open}
                onCancel={() => setOpen(false)}
                width={panelWidth}
                closable={false}
                destroyOnClose
                bodyStyle={{
                    padding: 0,
                    overflow: "auto",
                    maxHeight: `calc(100vh - 220px`,
                }}
                style={{top: marginL}}
                footer={<Footer />}
            >
                <div className={css.selectedCourseDetails}>
                    <TransferCourseSelector currentCourseId={currentCourseId} selectedCourse={selectedCourse} setSelectedCourse={setSelectedCourse} />
                    <Card title={<div className={css.title}>{"Zu übertragende Buchungen"}</div>} className={css.transferCourseSelector}>
                        {/* Wie handleVoidAndMove: auch Buchungen mit aktivem Link werden nach dem Entwerten verschoben */}
                        <BulkDunningAlert bookingIds={transferableBookings.concat(bookingsWithActiveLink).map((booking) => booking.id)} />
                        {transferableBookingsCount > 0 ? (
                            <>{renderTable(transferableBookings as unknown as Array<BookingTableType>)}</>
                        ) : (
                            <h3
                                style={{
                                    margin: 15,
                                    width: "100%",
                                    textAlign: "center",
                                    color: red[3],
                                }}
                            >
                                {`Es gibt keine übertragbaren Teilnehmer`}
                            </h3>
                        )}
                        {selectedBookingsWithDuplicates.length > 0 && selectedCourse && (
                            <div style={{opacity: 0.9}}>
                                <h5 style={{margin: 15, marginTop: 30}}>{"Teilnehmer die nicht übertragbar sind, da diese bereits in den Zielkurs eingebucht sind"}</h5>
                                {renderTable(selectedBookingsWithDuplicates as unknown as Array<BookingTableType>, true)}
                            </div>
                        )}
                        {/* Transfer-FK / Link-Void: Buchungen mit live Zahlungslink (entwertbar). */}
                        {bookingsWithActiveLink.length > 0 && selectedCourse && (
                            <div style={{opacity: 0.95}}>
                                <h5 style={{margin: 15, marginTop: 30}}>{i18n.containers.bookings.TransferMolliePaymentLink.bulk.activeLinkBucketTitle()}</h5>
                                <p style={{margin: "0 15px 10px"}}>{i18n.containers.bookings.TransferMolliePaymentLink.bulk.activeLinkBucketHint()}</p>
                                {renderTable(bookingsWithActiveLink as unknown as Array<BookingTableType>)}
                            </div>
                        )}
                        {/* Transfer-FK / Link-Void: bereits bezahlte Buchungen (nicht übertragbar, B-223). */}
                        {bookingsBlockedByPaidMollie.length > 0 && selectedCourse && (
                            <div style={{opacity: 0.9}}>
                                <h5 style={{margin: 15, marginTop: 30}}>{i18n.containers.bookings.TransferMolliePaymentLink.bulk.blockedByPaidBucketTitle()}</h5>
                                <p style={{margin: "0 15px 10px"}}>{i18n.containers.bookings.TransferMolliePaymentLink.bulk.blockedByPaidBucketHint()}</p>
                                {renderTable(bookingsBlockedByPaidMollie as unknown as Array<BookingTableType>, true)}
                            </div>
                        )}
                    </Card>
                </div>
                {selectedCourse && wizardBookingIds && (
                    <BulkTransferWizard
                        open
                        currentCourseId={currentCourseId}
                        targetCourse={selectedCourse}
                        bookingIds={wizardBookingIds}
                        onClose={() => setWizardBookingIds(null)}
                        onFinished={() => {
                            setWizardBookingIds(null);
                            onFinished();
                            setOpen(false);
                        }}
                    />
                )}
            </Modal>
        );
    }

    return (
        <Tooltip title="Teilnehmer umbuchen">
            <Button
                key={key}
                onClick={() => setOpen(true)}
                size="small"
                type="link"
                style={{
                    fontSize: 15,
                }}
                shape="circle"
                className={btnClassName}
            >
                <SwapOutlined /* className={css.shimeringSmallItems} */ />
            </Button>
        </Tooltip>
    );
};

export default memo(TransferMultipleBookings);
